<template>
  <div class="table-container" :class="{ 'table-widget-container': isTableWidget }">
    <v-data-table
      :headers="headers"
      :items="data"
      :items-per-page="rowsPerPage"
      class="elevation-0 data-table__table"
      :class="{
        striped: isStriped,
        'hide-footer': isTableWidget,
        'no-bottom-padding': isTableWidget,
        'no-summary-row': !isShowSummaryRow,
        'management-table': !isTableWidget,
        'comparison-table': comparisonWidget,
      }"
      :width="'100%'"
      :sort-desc="sort.sortDesc"
      :sort-by="sort.sortBy"
      fixed-header
      must-sort
      @page-count="pageCount = $event"
      hide-default-header
      :hide-default-footer="hideFooter"
      :search="search"
      :footer-props="footerProps"
      @click:row="onSelect"
    >
      <template #header="{ props: { headers } }">
        <thead class="v-data-table-header">
          <tr :ref="`sort_key${widgetId}`">
            <th
              class="data-table__th"
              :class="{
                'data-table__th-hide': isWidgetLoading,
                'data-table__not-sortable': header.sortable === false,
                [headerAlignClass(header)]: true,
              }"
              v-for="header in headers"
              :key="header.value"
              scope="col"
              @click="onHeaderClick(header.value)"
              grid-drag-ignore
            >
              {{ header.text }}
              <v-icon
                v-if="!hideIcons"
                small
                class="data-table__sort-icon"
                :class="{ active: header.value === sort.sortBy, desc: sort.sortDesc }"
                >mdi-arrow-up</v-icon
              >
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:[`item.${item.value}`]="{ value, header }" v-for="item in headers">
        <td :key="item.value">
          <span v-if="header.format">{{ value | useFormatter(header.format) }}</span>
          <span v-else :class="{ 'cell-display-metrics': isDisplayMetric(header.value) }">{{
            metricFormatter(header.value, value)
          }}</span>
        </td>
      </template>

      <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
        <slot :name="name" v-bind="data"></slot>
      </template>

      <template v-slot:foot>
        <tr v-if="isShowSummaryRow" class="cell-summary-wrapper">
          <td
            class="cell-summary-row"
            :class="{ 'cell-summary-row-hide': isWidgetLoading }"
            v-for="header in headers"
            :key="header.value"
          >
            <span v-if="summaryRow[header.value]" :class="{ 'cell-display-metrics': isDisplayMetric(header.value) }">{{
              metricFormatter(header.value, Number(summaryRow[header.value]))
            }}</span>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import Sortable from "sortablejs";
import Vue from "vue";
import { DataOptions } from "vuetify";
import { mapGetters } from "vuex";

import { LabelType } from "@/components/widgets/explore/TableWidget.vue";
import { formatters } from "@/helpers/formatters";
import Lang from "@/lang";
import inputsDictionary from "@/settings/inputsDictionary";
import { metricsDictionary } from "@/settings/metricsDictionary";
import { SortType, tableRows } from "@/settings/tableSettings";
import { TemplateConfig } from "@/store/modules/dashboardsTemplates";
import { InputDictionary } from "@/types/common";
import { ContentWidget } from "@/types/layouts";

export type TableHeader = {
  title: string;
  value: string;
};

export default Vue.extend({
  name: "BaseTable",

  props: {
    content: Object as () => ContentWidget,
    edit: Boolean,
    data: Array,
    headers: Array,
    headerLabels: Array,
    rowsPerPage: Number,
    isWidgetLoading: Boolean,
    summaryRow: Object,
    search: String,
    report: Boolean,
    isTableWidget: Boolean,
    comparisonWidget: {
      type: Boolean,
      default: false,
    },
    hideIcons: {
      type: Boolean,
      default: false,
    },
    footerProps: Object,
    hideFooter: Boolean,
    sortParam: Object,
  },

  watch: {
    rowsPerPage: {
      handler(val): void {
        this.onUpdateItemRows(val);
      },
    },
    sort: {
      deep: true,
      handler(val: SortType): void {
        this.options.page = 1;
        this.$emit("onSortChange", { sort: val, id: this.widgetId });
      },
    },
  },

  beforeMount() {
    if (this.content?.settings?.["sort"]) {
      this.sort = this.content.settings.sort as SortType;
    }

    if (this.sortParam) {
      this.sort = { ...this.sortParam };
    }
  },

  mounted() {
    if (this.isTableWidget) {
      this.$nextTick(() => {
        const element = this.$refs[`sort_key${this.widgetId}`] as HTMLElement;
        Sortable.create(element as HTMLElement, {
          onEnd: ({ newIndex, oldIndex }) => {
            this.$emit("onColumnsOrderChange", { newIndex: newIndex, oldIndex: oldIndex });
          },
        });
      });
    }
  },

  data() {
    return {
      Lang,
      tableRows,
      pageCount: 1,
      options: {} as DataOptions,
      sort: {
        sortBy: "",
        sortDesc: true,
      },
      displayMetrics: inputsDictionary.displayMetrics,
      timeMetrics: inputsDictionary.timeMetrics,
      page: 1,
    };
  },

  filters: {
    useFormatter(value: string, formatter: string): string | number {
      return formatters[formatter](value);
    },
  },
  computed: {
    ...mapGetters("userSettings", ["userSettings", "dashboards"]),

    isStriped(): boolean {
      return !!this.content?.settings.striped;
    },

    isShowSummaryRow(): boolean {
      return !!this.content?.settings?.summaryRow;
    },

    widgetId(): string {
      return this.content?.id.replace(" ", "");
    },

    isCompactFormat(): boolean {
      return (this.content?.settings?.compactFormat as boolean | undefined) ?? false;
    },
  },
  methods: {
    onHeaderClick(header: string): void {
      this.sort.sortDesc = !this.sort.sortDesc;
      this.sort.sortBy = header;
      this.options = { ...this.options, sortBy: [header], sortDesc: [this.sort.sortDesc] };
    },

    onColumnsChange(val: TableHeader[]): void {
      this.$emit("onColumnsChange", val);
    },

    metricFormatter(metric: string, value: number): string | number {
      let inputDictionary = {} as InputDictionary | undefined;
      inputDictionary = metricsDictionary.find((item: InputDictionary) => item.value === metric);

      if (inputDictionary?.formatter) {
        return this.isCompactFormat
          ? inputDictionary.formatter(value, { notation: "compact" })
          : inputDictionary.formatter(value);
      } else {
        return value;
      }
    },

    onUpdateItemRows(val: number): void {
      this.options.page = 1;
      this.options.itemsPerPage = val;
    },

    async onUpdate(): Promise<void> {
      await this.$emit("onUpdate");
    },

    onSelect(val: TemplateConfig): void {
      this.$emit("select", val);
    },

    isDisplayMetric(metric: string): boolean {
      return !!inputsDictionary.displayMetrics.find((item: InputDictionary) => item.value === metric);
    },

    headerAlignClass(header: LabelType) {
      return this.isDisplayMetric(header.value) ? "data-table__th-align-right" : "data-table__th-align-left";
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme.scss";
@import "@/scss/custom_variables.scss";
.data-table {
  &__table {
    height: 100%;
  }
  &__not-sortable {
    pointer-events: none;
  }
  &__th {
    font-size: 14px;
    cursor: pointer;
  }
  &__th-hide {
    display: none;
  }
  &__th:hover &__sort-icon {
    opacity: 1;
    color: var(--v-accent-global-base);
  }
  &__th-align-left {
    text-align: left !important;
  }
  &__th-align-right {
    text-align: right !important;
  }
  &__sort-icon.active {
    opacity: 1;
  }
  &__sort-icon.desc {
    transform: rotate(180deg);
  }
  &__sort-icon {
    opacity: 0;
  }
  .table-container {
    display: block;
    height: 100%;
    position: relative;
  }
  .table-widget-container {
    display: block;
    height: 100%;
    width: 100%;
  }
  .pagination {
    display: flex;
    align-items: center;
    order: 2;
    background-color: var(--v-content-light);
    border-radius: 4px;
    .inner-container {
      display: flex;
      align-items: center;
      padding: 0 24px;
    }
    .v-icon {
      cursor: pointer;
    }
    .v-icon:first-of-type {
      height: 20px;
    }
    .v-icon:last-of-type {
      height: 20px;
    }
  }
  .page-info {
    font-size: 14px;
    font-weight: 500;
    color: #70748f;
    padding: 10px 16px;
  }
  ::v-deep {
    th {
      white-space: nowrap;
    }
  }
  .cell-summary-row {
    position: sticky;
    bottom: 0;
    padding: 0 16px;
    font-weight: 500;
    height: $row-height;
    border-top: thin solid rgba(255, 255, 255, 0.12);
  }
  .cell-summary-wrapper {
    z-index: 1;
    position: relative;
    top: 0;
  }
  .cell-summary-row-hide {
    display: none;
  }
}
.cell-display-metrics {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
</style>

<style lang="scss">
@import "@/scss/theme.scss";
.data-table__th {
  font-size: 14px !important;
}
.v-application.theme--dark,
.pdf-container.theme--dark {
  .data-table {
    &__th {
      font-size: 14px;
      white-space: nowrap;
    }
    .select.v-input__control {
      border-radius: 4px !important;
    }
    td {
      color: var(--v-content-light);
      &.disabled span {
        opacity: 0.4;
      }
    }
    thead > tr > th {
      color: rgba(255, 255, 255, 0.7);
      background: var(--v-background-dark-base);
    }
    thead > tr > th:hover {
      transition: all linear 0.1s;
      color: var(--v-accent-global-base) !important;
    }
    .pagination {
      background-color: var(--v-background-light-base);
    }
    .page-info {
      color: var(--v-content-white-base);
    }
    tr.v-data-table__selected {
      background-color: var(--v-background-dark-base);
    }
    .striped tr:nth-of-type(even) {
      background-color: var(--v-background-light-base);
    }
    tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
      background-color: var(--v-background-light-base) !important;
    }
    .cell-summary-row {
      background-color: var(--v-background-medium-base);
    }
    .v-data-footer {
      background-color: var(--v-background-dark-base);
    }
  }
}
.v-application.theme--light,
.pdf-container.theme--light {
  .data-table {
    &__th {
      white-space: nowrap;
    }
    .striped tr:nth-of-type(even) {
      background-color: var(--v-content-light-base);
    }
    th:hover {
      transition: all linear 0.1s;
      color: var(--v-accent-global-base) !important;
    }
    tr:hover {
      background-color: var(--v-content-light-base) !important;
    }
    .cell-summary-row {
      background-color: var(--v-background-medium-base);
      border-top: thin solid rgba(0, 0, 0, 0.12) !important;
      border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
    }
  }
}
.v-data-footer__select {
  margin-left: auto;
  margin-right: 14px;
}
.data-table .v-data-footer__pagination {
  margin-left: unset;
}

.v-data-table__wrapper {
  height: calc(100% - 36px);
  width: 100%;
  overflow-y: scroll;
  position: absolute;
  padding-bottom: 0;
}

.data-table__table {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.v-data-footer {
  position: sticky !important;
  top: 100%;
  right: 0;
  width: 100%;
  padding-bottom: $half-row-height;
  padding-top: $row-height !important;
}

.hide-footer {
  .v-data-footer {
    border-top: unset;
  }
  .v-data-footer__select {
    display: none;
    margin-left: auto;
    margin-right: 14px;
  }
  .v-data-footer__pagination {
    margin-left: auto !important;
  }
}
.no-bottom-padding {
  .v-data-footer {
    padding-bottom: 0;
  }
}
.v-data-footer {
  padding-bottom: unset;
  padding-top: unset !important;
}
.no-bottom-padding > .v-data-table__wrapper {
  height: calc(100% - 36px) !important;
}
.management-table > .v-data-table__wrapper {
  height: calc(100% - 64px) !important;
}

.comparison-table {
  .data-table__th-align-left {
    text-align: unset !important;
  }
  thead tr th,
  tbody tr td:not(:first-child) {
    text-align: right !important;
  }
}
</style>
